import { useMemo, useState } from 'react'
import { capture, markDone, planItem, updateItem } from '../api/client'
import { ItemCard, NextActionCard, Tags, type ProjectTitles } from '../components/ItemCard'
import ProjectSheet from '../components/ProjectSheet'
import { matches } from '../search'
import type { Item, Project, State } from '../types'

interface Props {
  state: State | null
  onEdit: (item: Item) => void
  onReclarify: (id: string) => void
  onChanged: () => void
}

/** Axe de classement : par projet, ou par zone de vie. */
type Axis = 'project' | 'domain'
/** 'all' | 'none' (sans projet / sans domaine) | id de projet | nom de domaine */
type Filter = string

const NONE = 'none'

export default function Lists({ state, onEdit, onReclarify, onChanged }: Props) {
  const [axis, setAxis] = useState<Axis>('project')
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [addingTo, setAddingTo] = useState<Project | null>(null)
  const [newTask, setNewTask] = useState('')

  const projectTitles: ProjectTitles = useMemo(() => {
    const map: ProjectTitles = {}
    for (const p of state?.projects ?? []) map[p.id] = p.title
    return map
  }, [state])

  if (!state) return <div className="empty">Chargement…</div>

  const items = state.items
  const active = state.projects.filter((p) => p.status === 'active')
  const nexts = items.filter((i) => i.status === 'next')

  const done = async (id: string) => {
    if (busy) return
    setBusy(true)
    try {
      await markDone(id)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  const plan = async (id: string, date: 'today' | 'tomorrow') => {
    if (busy) return
    setBusy(true)
    try {
      await planItem(id, date)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  // Capture puis rattachement : le back n'a pas d'endpoint qui fasse les deux, et
  // /api/capture est le seul chemin qui pose created_at correctement.
  const addTask = async () => {
    const title = newTask.trim()
    if (!title || !addingTo || busy) return
    setBusy(true)
    try {
      const c = await capture(title)
      await updateItem(c.item.id, { project_id: addingTo.id, status: 'next' })
      setNewTask('')
      setAddingTo(null)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  /** Le filtre courant, appliqué à n'importe quel lot de tâches. */
  const inFilter = (i: Item) => {
    if (filter === 'all') return true
    if (axis === 'project') return filter === NONE ? !i.project_id : i.project_id === filter
    return filter === NONE ? !i.domain : i.domain === filter
  }

  // Changer d'axe remet la valeur à zéro : un id de projet n'a aucun sens sur
  // l'axe domaine, et le filtre deviendrait invisible.
  const switchAxis = (a: Axis) => {
    setAxis(a)
    setFilter('all')
  }

  const domains = [...new Set(nexts.map((i) => i.domain).filter((d): d is string => !!d))].sort((a, b) =>
    a.localeCompare(b, 'fr'),
  )

  const chip = (f: Filter, label: string, n: number) => (
    <button key={f} className={'fchip' + (filter === f ? ' on' : '')} onClick={() => setFilter(f)}>
      {label} <span className="fn">{n}</span>
    </button>
  )

  const searching = query.trim().length > 0
  const filterLabel =
    filter === 'all'
      ? null
      : filter === NONE
        ? axis === 'project'
          ? 'Sans projet'
          : 'Sans domaine'
        : axis === 'project'
          ? (projectTitles[filter] ?? filter)
          : filter

  const searchBar = (
    <div className="searchbar">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher une tâche…"
        aria-label="Rechercher une tâche"
      />
    </div>
  )

  const selectors = (
    <>
      <div className="sec">Prochaines actions</div>
      <div className="fchips">
        <button className={'fchip' + (axis === 'project' ? ' on' : '')} onClick={() => switchAxis('project')}>
          Projet
        </button>
        <button className={'fchip' + (axis === 'domain' ? ' on' : '')} onClick={() => switchAxis('domain')}>
          Domaine
        </button>
      </div>
      <div className="fchips">
        {chip('all', 'Tout', nexts.length)}
        {axis === 'project'
          ? (() => {
              const n = nexts.filter((i) => !i.project_id).length
              return n > 0 ? chip(NONE, 'Sans projet', n) : null
            })()
          : (() => {
              const n = nexts.filter((i) => !i.domain).length
              return n > 0 ? chip(NONE, 'Sans domaine', n) : null
            })()}
        {axis === 'project'
          ? active.map((p) => {
              const n = nexts.filter((i) => i.project_id === p.id).length
              return n > 0 ? chip(p.id, p.title, n) : null
            })
          : domains.map((d) => chip(d, d, nexts.filter((i) => i.domain === d).length))}
      </div>
    </>
  )

  // --- Recherche : elle balaie TOUT, y compris les faites et les en attente.
  // Une recherche qui ne regarde que la liste affichée ne sert à rien : on
  // cherche justement ce qu'on ne voit pas.
  if (searching) {
    const hits = items.filter((i) => i.status !== 'trash' && matches(i, query))
    const here = filterLabel ? hits.filter(inFilter) : []
    const elsewhere = filterLabel ? hits.filter((i) => !inFilter(i)) : hits

    const row = (i: Item) => (
      <div className="card" key={i.id}>
        <div className="body">
          <div className="t">
            {i.next_action ?? i.title}
            <span className="tag">{i.status}</span>
            <Tags item={i} />
          </div>
          {i.next_action && i.next_action !== i.title && <div className="m">{i.title}</div>}
        </div>
        <button className="ebtn" onClick={() => onEdit(i)}>
          ✎
        </button>
      </div>
    )

    return (
      <>
        {searchBar}
        {selectors}
        {hits.length === 0 && <div className="empty">Aucune tâche ne correspond à « {query.trim()} ».</div>}
        {filterLabel && here.length > 0 && (
          <>
            <div className="sec">Dans « {filterLabel} » ({here.length})</div>
            {here.map(row)}
          </>
        )}
        {elsewhere.length > 0 && (
          <>
            <div className="sec" style={{ marginTop: filterLabel && here.length > 0 ? 18 : 0 }}>
              {filterLabel ? `Ailleurs (${elsewhere.length})` : `Résultats (${elsewhere.length})`}
            </div>
            {elsewhere.map(row)}
          </>
        )}
      </>
    )
  }

  const shown = nexts.filter(inFilter)
  const waiting = items.filter((i) => i.status === 'waiting')
  const someday = items.filter((i) => i.status === 'someday')

  return (
    <>
      {searchBar}
      {selectors}
      {shown.length === 0 && <div className="empty">Rien ici.</div>}
      {shown.map((i) => (
        <NextActionCard
          key={i.id}
          item={i}
          projects={projectTitles}
          onEdit={onEdit}
          onReclarify={onReclarify}
          onDone={done}
          onPlan={(id, d) => void plan(id, d)}
        />
      ))}

      <div className="sec" style={{ marginTop: 22 }}>
        Projets
      </div>
      {active.length === 0 && <div className="empty">Aucun projet.</div>}
      {active.map((p) => {
        const steps = items.filter((i) => i.status === 'next' && i.project_id === p.id)
        const upcoming = items.filter((i) => i.status === 'blocked' && i.project_id === p.id)
        return (
          <div className="proj" key={p.id}>
            <div className="head">
              <h3>{p.title}</h3>
              <button className="ebtn" onClick={() => setAddingTo(p)} title="Ajouter une tâche à ce projet">
                +
              </button>
              <button className="ebtn" onClick={() => setEditingProject(p)} title="Modifier le projet">
                ✎
              </button>
            </div>
            <div className="out">{p.outcome ?? ''}</div>
            {steps.length === 0 && upcoming.length === 0 && <div className="step up">Aucune action.</div>}
            {steps.map((i) => (
              <div className="step row" key={i.id}>
                <span className="lab">
                  → {i.next_action ?? i.title}
                  <Tags item={i} />
                  {i.context && <span className="pctx"> {i.context}</span>}
                </span>
                <button className="ebtn" onClick={() => onReclarify(i.id)} title="Re-clarifier / transformer en projet">
                  ↻
                </button>
                <button className="ebtn" onClick={() => onEdit(i)}>
                  ✎
                </button>
                <button className="dbtn" onClick={() => void done(i.id)}>
                  ✓
                </button>
              </div>
            ))}
            {upcoming.map((i) => (
              <div className="step up" key={i.id}>
                ⏳ à venir : {i.next_action ?? i.title}
              </div>
            ))}
          </div>
        )
      })}

      {waiting.length > 0 && (
        <>
          <div className="sec">En attente</div>
          {waiting.map((i) => (
            <ItemCard key={i.id} item={i} projects={projectTitles} onEdit={onEdit} onReclarify={onReclarify} onDone={done} />
          ))}
        </>
      )}
      {someday.length > 0 && (
        <>
          <div className="sec">Un jour</div>
          {someday.map((i) => (
            <ItemCard key={i.id} item={i} projects={projectTitles} onEdit={onEdit} onReclarify={onReclarify} onDone={done} />
          ))}
        </>
      )}
      {editingProject && (
        <ProjectSheet project={editingProject} onClose={() => setEditingProject(null)} onChanged={onChanged} />
      )}
      {addingTo && (
        <div className="sheet" onClick={(e) => e.target === e.currentTarget && setAddingTo(null)}>
          <div className="panel">
            <h3>Nouvelle tâche — {addingTo.title}</h3>
            <input
              autoFocus
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void addTask()}
              placeholder="Prochaine action concrète…"
            />
            <div className="actions">
              <button className="save" onClick={() => void addTask()} disabled={busy || !newTask.trim()}>
                Ajouter
              </button>
              <button className="cancel" onClick={() => setAddingTo(null)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
